import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { useRef, useState } from 'react'
import { useTracking } from 'lib/babylistHealthAnalytics'
import { Controller, useForm } from 'react-hook-form'
import Checkbox from 'baby-ui/foundation/CheckBox'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import LoadingButton from 'baby-ui/compounds/LoadingButton'
import TermsAndConditions from 'baby-ui/compounds/TermsAndConditions'
import { useMutation } from 'react-query'
import useCheckout from './useCheckout'
import apiErrorResponseAdapter from '../../utils/apiErrorResponseAdapter'
import { errorThrownEvent } from '../../events'
import { ReplacementPartsFormSchema } from './validationSchema'
import useStyles from './styles'
import { useLayout } from '../../widgets/Layout'
import { useDMEStore } from '../../store'
import { getRemainingOverlayTime } from '../CustomerInformationForm/CustomerInformationForm.utils'
import { verifyOrderInsurance } from '../../requests'
import InvalidPartsOrderView from '../../views/InvalidPartsOrderView'

export interface ReplacementPartsFormProps {
  order: APIResponse.DME.Order
  productOptions: APIResponse.CatalogCategoryProduct[]
  customerInsuranceId: string
  subscriptionId?: string
}

export interface ReplacementPartsFormValues {
  productId?: APIResponse.HeroProduct['id']
  aobAcknowledged: boolean
}

const ReplacementPartsForm = observer(
  ({
    order,
    customerInsuranceId,
    productOptions,
    subscriptionId,
  }: ReplacementPartsFormProps) => {
    const classes = useStyles()
    const layout = useLayout()
    const store = useDMEStore()
    const theme = useTheme()
    const tracker = useTracking()
    const { control, errors, handleSubmit, setValue, watch } =
      useForm<ReplacementPartsFormValues>({
        defaultValues: {
          aobAcknowledged: false,
          productId: undefined,
        },
        validationSchema: ReplacementPartsFormSchema,
      })
    const [formErrorMessage, setFormErrorMessage] = useState<string>('')
    const [insuranceVerificationError, setInsuranceVerificationError] =
      useState<APIResponse.StandardError | null>(null)
    const [showAOBDialog, setShowAOBDialog] = useState<boolean>(false)
    const timerRef = useRef<ReturnType<typeof setInterval>>()
    const [submitStart, setSubmitStart] = useState<number>()

    const watchProductId = watch('productId')
    const watchAobAcknowledged = watch('aobAcknowledged')

    const onAgreeToAOB = () => {
      setValue('aobAcknowledged', true)
      setShowAOBDialog(false)
    }
    const onDisagreeToAOB = () => {
      setValue('aobAcknowledged', false)
      setShowAOBDialog(false)
    }

    const {
      checkout,
      isLoading: isCheckoutLoading,
      isNavigating,
    } = useCheckout(
      {
        sourceOrderId: order.id,
        productId: watchProductId,
        aobAcknowledged: watchAobAcknowledged,
        subscriptionId,
      },
      (error) => {
        const adaptedError = apiErrorResponseAdapter(error)
        errorThrownEvent(tracker)({
          error: adaptedError.message,
        })
        setFormErrorMessage(adaptedError.message)
      }
    )

    const waitMinimumOverlayTime = (
      callback: (e?: APIResponse.StandardError) => void
    ) => {
      const waitTime = getRemainingOverlayTime(submitStart)
      timerRef.current = setTimeout(() => {
        store.ui.globalComponents.Overlay.close('insuranceVerification')
        callback()
      }, waitTime)
    }

    const onSubmit = () => {
      if (layout?.isDmeReplacementPartsGaEnabled) {
        store.ui.globalComponents.Overlay.show('insuranceVerification')
        setFormErrorMessage('')
        setSubmitStart(new Date().getTime())
        verifyInsurance({ customerInsuranceId })
      } else {
        checkout()
      }
    }

    const onVerifyInsuranceSuccess = () => {
      waitMinimumOverlayTime(() => {
        checkout()
      })
    }

    const onVerifyInsuranceError = (e?: APIResponse.StandardError) => {
      waitMinimumOverlayTime(() => {
        setInsuranceVerificationError(
          e || {
            error: { message: 'Something went wrong', title: 'Unknown Error' },
          }
        )
      })
    }

    const [verifyInsurance, { isLoading: isInsuranceLoading }] = useMutation(
      verifyOrderInsurance,
      {
        onSuccess: onVerifyInsuranceSuccess,
        onError: onVerifyInsuranceError,
      }
    )

    const handleProductIdChange = (
      event: React.ChangeEvent<HTMLSelectElement>
    ) => {
      const newValue = Number(event.target.value)
      setValue('productId', newValue, true)
    }

    if (insuranceVerificationError) return <InvalidPartsOrderView />

    return (
      <Box
        bgcolor={theme.palette.background.contrast}
        px={{ xs: 4, sm: 0 }}
        py={8}
      >
        <Box className={classes.formContainer}>
          <TermsAndConditions
            clauses={[
              {
                title: (
                  <p>
                    By digitally signing this document you acknowledge that you
                    are still using your breast pump and are in need of
                    replacement parts to keep your breast pump functioning to
                    the best of its capabilities.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
              {
                title: (
                  <p>
                    You also authorize Babylist Health LLC and Medequip Inc., a
                    member of Babylist's Preferred Durable Medical Equipment
                    Network, to submit claims to your insurance company on your
                    behalf and request on your behalf all insurance information,
                    prescriptions and benefits for products provided as well as
                    to release your medical records to any person, organization,
                    company and/or agency which may be involved in providing
                    your care.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
              {
                title: (
                  <p>
                    You also acknowledge that you have not ordered breast pump
                    replacement parts through another DME provider.
                  </p>
                ),
                type: 'unordered',
                terms: [],
              },
            ]}
            open={showAOBDialog}
            title="Replacement Parts Use Acknowledgement"
            onAgree={onAgreeToAOB}
            onDisagree={onDisagreeToAOB}
          />

          <Typography
            className={classes.formTitle}
            color="primary"
            component="h1"
            variant="h4"
          >
            You're eligible for free replacement parts!
          </Typography>
          <Typography
            className={classes.formSubtitle}
            color="primary"
            variant="body2"
          >
            You'll receive FREE Spectra replacement parts through your insurance
            and we'll even cover the shipping.
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            {formErrorMessage && (
              <Alert severity="error" variant="standard">
                <span>{formErrorMessage}</span>
              </Alert>
            )}
            <div className={classes.selectInput}>
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="productId">
                  Select your flange size:{' '}
                </InputLabel>
                <Controller
                  aria-required
                  required
                  as={(props) => (
                    <Select {...props} native onChange={handleProductIdChange}>
                      <option aria-label="None" />
                      {productOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.attributes?.size}
                        </option>
                      ))}
                    </Select>
                  )}
                  control={control}
                  id="productId"
                  label="Select your flange size"
                  name="productId"
                />
                {errors.productId && (
                  <FormHelperText>{errors.productId.message}</FormHelperText>
                )}
              </FormControl>
            </div>

            <Typography
              className={classes.partsText}
              component="div"
              variant="body2"
            >
              If you are still using your pump, your insurance can cover the
              following items:
              <ul>
                <li>2 sets of breast flanges</li>
                <li>2 sets of tubing</li>
                <li>2 backflow protectors</li>
                <li>2 duckbill valves</li>
                <li>2 wide neck milk bottles with cap, disc, and lid</li>
              </ul>
            </Typography>

            <FormControl fullWidth className={classes.checkbox}>
              <Controller
                required
                as={Checkbox}
                control={control}
                label={
                  <Typography variant="caption">
                    By checking this box, I confirm that I am still pumping and
                    require breast pump replacement parts. I also agree to the{' '}
                    {/* component=button means this is rendering a button, not an anchor */}
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      variant="inherit"
                      onClick={() => {
                        setShowAOBDialog(true)
                      }}
                    >
                      Assignment of Benefits
                    </Link>
                    .
                  </Typography>
                }
                name="aobAcknowledged"
              />
              {errors.aobAcknowledged && (
                <FormHelperText>
                  {errors.aobAcknowledged.message}
                </FormHelperText>
              )}
            </FormControl>
            <LoadingButton
              fullWidth
              color="secondary"
              disabled={
                !watchProductId ||
                !watchAobAcknowledged ||
                isCheckoutLoading ||
                isInsuranceLoading ||
                isNavigating
              }
              loading={isCheckoutLoading || isInsuranceLoading}
              type="submit"
              variant="contained"
            >
              Continue
            </LoadingButton>
          </form>
        </Box>
      </Box>
    )
  }
)

export default ReplacementPartsForm
